import {
  createContext,
  createRef,
  FC,
  RefObject,
  useContext,
  useState,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { clientEnv } from '@config/client';
import axios from 'axios';

export const RecaptchaContext = createContext<{
  recaptchaPassed: boolean;
  recaptchaExecuted: boolean;
  verifyRecaptcha: () => Promise<boolean>;
}>({
  recaptchaPassed: false,
  recaptchaExecuted: false,
  verifyRecaptcha: () => Promise.reject(new Error()),
});

const RecaptchaContextProvider: FC = ({ children }) => {
  const [isRecaptchaProcessed, setRecaptchaProcessed] = useState(false);
  const [isRecaptchaVerified, setRecaptchaVerified] = useState(false);
  const recaptchaRef: RefObject<ReCAPTCHA> = createRef();

  const verifyRecaptcha = async (): Promise<boolean> => {
    const recaptchaToken = await recaptchaRef.current?.executeAsync();

    if (!recaptchaToken) return false;

    const verifyResult = await axios.post<{ data: []; error: string }>(
      '/api/recaptcha/verify',
      { token: recaptchaToken },
    );

    setRecaptchaProcessed(true);

    if (verifyResult.data.error) {
      setRecaptchaVerified(false);
      return false;
    }

    setRecaptchaVerified(true);
    return true;
  };

  const siteKey = clientEnv()?.GOOGLE_RECAPTCHA_SITE_KEY;

  if (!siteKey) return null;

  return (
    <RecaptchaContext.Provider
      value={{
        recaptchaPassed: isRecaptchaVerified,
        recaptchaExecuted: isRecaptchaProcessed,
        verifyRecaptcha,
      }}
    >
      {children}

      <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={siteKey} />
    </RecaptchaContext.Provider>
  );
};

export const useRecaptcha = () => {
  return useContext(RecaptchaContext);
};

export default RecaptchaContextProvider;
